import { formatAmount } from "@loot-os/api-contract";
import { Button } from "@loot-os/ui/components/button";
import { Card, CardContent } from "@loot-os/ui/components/card";
import { Input } from "@loot-os/ui/components/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@loot-os/ui/components/table";
import { formatNumber } from "@loot-os/ui/lib/format";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { StatusBadge } from "../components/badges";
import { ErrorAlert } from "../components/ErrorAlert";
import { TableMessageRow } from "../components/TableMessageRow";
import { amountColumnHeader, useCurrency } from "../lib/currency";
import { formatDate, formatOdds } from "../lib/format";
import { useTRPC } from "../lib/trpc";
import { useDebounced } from "../lib/useDebounced";

/**
 * The environment's packs, as the tenant's own product mirrors them through
 * `PUT /packs/{externalId}`. Read-only: packs are authored in that product.
 */
export function PacksPage() {
  const trpc = useTRPC();
  const currency = useCurrency();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounced(search.trim());

  const packs = useQuery(
    trpc.packs.list.queryOptions(
      { search: debouncedSearch || undefined },
      { placeholderData: (previous) => previous },
    ),
  );
  const isStale = packs.isPlaceholderData;

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Packs</h1>
          <p className="text-sm text-muted-foreground">
            Packs your own product sends to Loot OS through the pack mirror. Edit them there.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isStale && <span className="text-sm text-muted-foreground">Searching…</span>}
          {search && (
            <Button type="button" variant="ghost" size="sm" onClick={() => setSearch("")}>
              Clear
            </Button>
          )}
          <Input
            className="w-64"
            type="search"
            placeholder="Search name, id or external id"
            maxLength={256}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {packs.error && <ErrorAlert message={packs.error.message} />}

      <Card>
        <CardContent>
          <Table
            aria-busy={isStale}
            className={`transition-opacity ${isStale ? "opacity-60" : ""}`}
          >
            <TableHeader>
              <TableRow>
                <TableHead className="w-12" />
                <TableHead>Name</TableHead>
                <TableHead>External id</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">
                  {amountColumnHeader("Price", currency)}
                </TableHead>
                <TableHead className="text-right" title="Common / uncommon / rare slots">
                  Cards (C / U / R)
                </TableHead>
                <TableHead className="text-right">God pack</TableHead>
                <TableHead className="text-right">Items</TableHead>
                <TableHead>Last synced</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {packs.isPending && <TableMessageRow colSpan={9}>Loading…</TableMessageRow>}
              {packs.data?.length === 0 && (
                <TableMessageRow colSpan={9}>
                  {debouncedSearch
                    ? `No packs match “${debouncedSearch}”.`
                    : "No packs in this environment. The pack mirror writes to the live environment only."}
                </TableMessageRow>
              )}
              {packs.data?.map((pack) => (
                <TableRow key={pack.id}>
                  <TableCell>
                    {pack.imageUrl ? (
                      <img
                        className="size-8 max-w-none rounded-md border object-cover"
                        src={pack.imageUrl}
                        alt=""
                      />
                    ) : null}
                  </TableCell>
                  <TableCell className="font-medium">{pack.name}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {pack.externalId}
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={pack.status} />
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {formatAmount(pack.priceCents, currency)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {formatNumber(pack.cardCount)} ({pack.commonCount} / {pack.uncommonCount} /{" "}
                    {pack.rareCount})
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {pack.godPackWeight ? formatOdds(pack.godPackWeight) : "—"}
                  </TableCell>
                  <TableCell
                    className="text-right font-mono text-xs"
                    title={
                      pack.externalItemCount
                        ? `${formatNumber(pack.externalItemCount)} not matched to a catalog item`
                        : undefined
                    }
                  >
                    {formatNumber(pack.itemCount)}
                    {pack.externalItemCount ? (
                      <span className="text-muted-foreground">
                        {" "}
                        ({formatNumber(pack.externalItemCount)} unmatched)
                      </span>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(pack.updatedAt)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
